import { NodeEdgeMapper } from '@iotv/datamodel-core';
import { AdjacentType, } from '@iotv/iotv-v3-types';
import { showCognitoUserId } from './aws/DebugAuth';
import ApiGateway from './aws/api-gateway/ApiGateway';
import { put } from './aws/s3/UserBlobs';
import { deleteObject, getAdjacent, getAdjacent2, getExact, getOne, link, save, searchByType, unlink } from './daoFunctions/daoFunctions';
import { getMultipleAdjacentTypes } from './daoFunctions/multipleAdjacentTypes';
const debug = process.env.REACT_APP_DEBUG && false;
/** @deprecated *See {@link AppDaoInstance} for more details* */
class AppDao {
    /**
     * Saves the `zombie` to the datastore
     *
     * *See {@link save | `ConsumerDao.save()`} for more details*
     */
    save;
    /**
     * Creates an edge from `zombieA` (source) to `zombieB` (target), and returns the source and edge items.
     *
     * *See {@link link | `ConsumerDao.link()`} for more details*
     */
    link;
    /**
     * Removes an edge from `zombieA` (source) to `zombieB` (target), and returns the source and edge items.
     *
     * *See {@link unlink | `ConsumerDao.unlink()`} for more details*
     */
    unlink;
    /**
     * Deletes the `zombie`, **and** any ascendent / descendent edges
     *
     * *See {@link deleteObject | `ConsumerDao.deleteObject()`} for more details*
     */
    deleteObject;
    /**
     * Searches from the `contextObject` for objects of `typeId`
     *
     * *See {@link searchByType | `ConsumerDao.searchByType()`} for more details*
     */
    searchByType;
    /**
     * Fetches objects adjacent to `object`, either parents or children
     *
     * *See {@link getAdjacent | `ConsumerDao.getAdjacent()`} for more details*
     */
    getAdjacent;
    /**
     * Fetches objects adjacent to a scope-defining context object, either parents or children
     *
     * *See {@link getAdjacent2Wrapper | `ConsumerDao.getAdjacent2Wrapper()`} for more details*
     */
    getAdjacent2;
    /**
     * Fetches multiple `types` adjacent to an `object`, either parents or children
     *
     * *See {@link getMultipleAdjacentTypes | `getMultipleAdjacentTypes()`} for more details*
     */
    getMultipleAdjacentTypes;
    /**
     * Fetches one `item` from the datastore
     *
     * *See {@link getOne | `ConsumerDao.getOne()`} for more details*
     */
    getOne;
    /**
     * Fetches one `Datastore Object Type` from the datastore
     *
     * *See {@link getExact | `ConsumerDao.getExact()`} for more details*
     */
    getExact;
    constructor() {
        this.save = save.bind(this);
        this.link = link.bind(this);
        this.unlink = unlink.bind(this);
        this.deleteObject = deleteObject.bind(this);
        this.searchByType = searchByType.bind(this);
        this.getAdjacent = getAdjacent.bind(this);
        this.getAdjacent2 = getAdjacent2.bind(this);
        this.getMultipleAdjacentTypes = getMultipleAdjacentTypes.bind(this);
        this.getOne = getOne.bind(this);
        this.getExact = getExact.bind(this);
    }
    /**
     * The main load function, which runs on startup.
     *
     * @remarks
     *
     * Uses the {@link user} and {@link userGroupRoles} to run the appropriate queries in the {@link roleArbitrationInstance} for the {@link currentGroup}
     * The responses are put into a new {@link NodeEdgeMapper} and replaces the state's existing application data (if any)
     */
    async updateStateFromApiGateway(user, userGroupRoles, roleArbitrationInstance, currentGroup) {
        debug &&
            console.log(`DAO TRANSACTION: updateStateFromApiGateway, roleArbitrationInstance has ${roleArbitrationInstance?.queries.length} queries`, {
                roleArbitrationInstance,
                currentGroup,
                user,
            });
        const initialNEM = new NodeEdgeMapper();
        if (debug)
            showCognitoUserId();
        const normalizedQueryPromises = [];
        if (user) {
            debug && console.log('updateStateFromApiGateway queries for user', roleArbitrationInstance?.queries);
            roleArbitrationInstance?.queries.forEach((query) => {
                normalizedQueryPromises.push(query(user));
            });
        }
        else {
            console.warn('updateStateFromApiGateway: no user!');
        }
        const normalizedQueryResults = await Promise.all(normalizedQueryPromises);
        debug && console.log('updateStateFromApiGateway initial query promises:', normalizedQueryPromises.length);
        normalizedQueryResults.forEach((queryResult) => {
            debug && console.log('updateStateFromApiGateway assigning:', queryResult);
            initialNEM.union(queryResult);
        });
        return initialNEM;
    }
    /**
     * Uploads the given {@link File} to the AWS S3 user blobs storage bucket
     *
     * @param directoryString fully-qualified directory path, e.g. `/path/to/files/`
     * @param file to upload
     * @returns a promise of a S3 key of uploaded file
     */
    async uploadToS3(directoryString, file) {
        const res = await put(directoryString, file);
        return { err: null, data: res.key };
    }
    /**
     * Runs a {@link query} on the datastore and calls back to {@link fn} with the results
     *
     * @remarks
     *
     * *See {@link getMainTableQueryResultsWithReturn | getMainTableQueryResultsWithReturn()} for an `async` version*
     *
     * @param fn callback to run with the results
     * @param query to run on the datastore
     */
    getMainTableQueryResults = async (fn, query) => {
        const apiRef = 'ConsumerApi';
        try {
            const path = '/baseQuery/';
            const response = await ApiGateway.post(path, query, apiRef);
            debug && console.log('getMainTableQueryResults query results', { contextRequest: query, response });
            fn(response);
        }
        catch (e) {
            debug && console.log('Error in getNetworkQueryResults query', e);
        }
    };
    /**
     * Runs a {@link query} on the datastore and asynchronously returns the results
     *
     * @remarks
     *
     * *See {@link getMainTableQueryResults | getMainTableQueryResults()} for a callback version*
     *
     * @param query to run on the datastore
     * @returns a promise of a {@link QueryCommandOutputNoMeta}
     */
    getMainTableQueryResultsWithReturn = async (query) => {
        const apiRef = 'ConsumerApi';
        const path = '/baseQuery/';
        return await ApiGateway.post(path, query, apiRef);
    };
    /**
     * Runs the {@link dcts | Device Control Transaction Requests}
     *
     * @remarks
     *
     * The return type for this will be an {@link ErrData}, not status `200`, data, etc. post-2023 05 Network Data API change
     *
     * @param dcts device control transactions to run
     * @returns a promise of a {@link MultipleDeviceControlTransactionRequestOutput}
     */
    submitDeviceTransaction = async (dcts) => {
        const path = `/deviceControl`;
        const res = await ApiGateway.post(path, dcts, 'NetworkApi');
        debug && console.log('submitDeviceTransaction res', res);
        /** currently knowingly, for whatever reason, not returning a result! */
        return res;
    };
    /**
     * Fetches the forecast weather from `OpenWeatherMap` for a geographic location (latitude, longitude)
     *
     * @param request containing the latitude and longitude to forecast for
     * @returns a promise of {@link OpenWeatherData}
     */
    getWeatherForecastByLatLng = async (request) => {
        let res = { err: null, data: null };
        if (request.location) {
            const apiRef = 'ConsumerApi';
            const path = `/weather/forecast`;
            res = await ApiGateway.post(path, request, apiRef);
        }
        else {
            res.err = new Error(`No location provided`);
        }
        debug && console.log('weather res', res);
        return res;
    };
    /**
     * Runs a graph traversal request on the datastore and provides the results in a {@link NodeEdgeMapper}
     *
     * @param request graph query
     * @returns a promise of a {@link NodeEdgeMapper} filled with the requested {@link ValidBusinessObject | ValidBusinessObjects}
     */
    queryByGraph = async function (request) {
        const res = { err: null, data: null };
        const path = '/graphQuery';
        const queryRes = (await ApiGateway.post(path, request));
        if (queryRes.data) {
            res.data = new NodeEdgeMapper();
            res.data.loadItems([...queryRes.data.hierarchy.edges, ...queryRes.data.hierarchy.nodes]);
        }
        else {
            res.err = queryRes.err;
        }
        return res;
    };
    /**
     * Used by {@link queryMultipleChildTypes | queryMultipleChildTypes()}
     *
     * *V020 addition?*
     * */
    doQuery = async function (contextObject, type, adjacencyType = AdjacentType.CHILD) {
        const res = { err: null, data: null };
        const path = '/graphQuery';
        const request = {
            contextObjects: [contextObject],
            path: [
                { objectTypeId: type, adjacencyType: adjacencyType },
                // { objectTypeId: 'Device', adjacencyType: AdjacentType.CHILD },
            ],
        };
        const queryRes = (await ApiGateway.post(path, request));
        if (queryRes.data) {
            res.data = [...queryRes.data.hierarchy.edges, ...queryRes.data.hierarchy.nodes];
        }
        else {
            res.err = queryRes.err;
        }
        return res;
    };
    /**
     * Fetches children of several {@link types} from the {@link contextObject}
     *
     * ***TODO**: make this a `Promise.all()` (so that the queries will run in parallel)*
     *
     * @param types
     * @param contextObject
     * @param adjacencyType
     * @returns
     */
    queryMultipleChildTypes = async (types, contextObject, adjacencyType = AdjacentType.CHILD) => {
        const res = { err: null, data: null };
        res.data = new NodeEdgeMapper();
        for (const type of types) {
            const q = await this.doQuery(contextObject, type, adjacencyType);
            if (q.data) {
                res.data.loadItems(q.data);
            }
            if (q.err) {
                // nothing to do
            }
        }
        return res;
    };
}
/**
 * @deprecated
 *
 * **Please use the specific `Datastore Access Object`s instead:**
 *
 * - **`ConsumerDAO`**
 * - **`NetworkDAO`**
 * - **`DeviceControlDAO`**
 * - **`SystemManagementDAO`**
 *
 * **App DAO**
 *
 * Provides various methods for interacting with the datastore
 *
 * Some methods for basic interactions are:
 * - {@link getOne | fetching an object}
 * - {@link link | linking two objects}
 * - {@link unlink | unlinking two objects}
 * - {@link save | saving an object}
 * - {@link deleteObject | deleting an object}
 *
 * There are also methods for querying the graph, such as:
 * - {@link getAdjacent | fetching objects of single adjacent type}
 * - {@link getMultipleAdjacentTypes | fetching objects of multiple adjacent types}
 *
 * More miscellaneous methods exist for e.g. graph queries, large objects, object histories, weather, etc.
 */
const AppDaoInstance = new AppDao();
export default AppDaoInstance;
